
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  CertificateInterface,
  CertificatesRepositoryInterface,
} from '@/services/SSL/interfaces';
import ViewCertificateDialog from '@/components/dialogs/ssl/ViewCertificateDialog.vue';
import DeleteCertificateDialog from '@/components/dialogs/ssl/DeleteCertificateDialog.vue';
import CertificateLogsDialog from '@/components/dialogs/ssl/CertificateLogsDialog.vue';

const HOVER_OPEN_MS = 1000;
const HOVER_CLOSE_MS = 220;

const certCache = new Map<number, CertificateInterface>();

@Component({
  inject: ['certificateRepository'],
  components: {
    ViewCertificateDialog,
    DeleteCertificateDialog,
    CertificateLogsDialog,
  },
})
export default class SslCertificateHover extends Vue {
  @Prop({ type: Number, default: null })
  public certificateId!: number | null;

  @Prop({ type: Number, required: true })
  public websiteId!: number;

  @Prop({ type: String, default: '' })
  public domain!: string;

  private certificateRepository!: CertificatesRepositoryInterface;

  public menuOpen = false;
  public loading = false;
  public renewing = false;
  public error: string | null = null;
  public certificate: CertificateInterface | null = null;
  public cardStyle: Record<string, string> = {};

  public certificateDialog: string = '';
  public certificateForDialog: CertificateInterface | false = false;
  public deleteCertificateDialog: string = '';
  public certificateForDeleteDialog: CertificateInterface | false = false;
  public certificateLogsDialog: string = '';
  public certificateForLogsDialog: CertificateInterface | false = false;

  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private dialogOpen = false;

  public get hasCertificate(): boolean {
    return this.certificateId !== null && this.certificateId !== undefined;
  }

  public get displayDomain(): string {
    if (this.certificate && this.certificate.domain) {
      return this.certificate.domain;
    }
    return this.domain || 'SSL certificate';
  }

  public get statusColor(): string {
    if (!this.certificate) {
      return 'grey';
    }
    return this.certificate.status === 'active' ? 'success' : 'grey';
  }

  public get altNamesText(): string {
    if (!this.certificate || !this.certificate.altNames) {
      return '';
    }
    return this.certificate.altNames
      .map((item: any) => (typeof item === 'string' ? item : (item && item.name) || ''))
      .filter(Boolean)
      .join(', ');
  }

  public get expiryInfo(): { text: string; color: string } {
    if (!this.certificate || !this.certificate.expiresAt) {
      return { text: 'No expiry', color: 'grey' };
    }

    const expires = new Date(this.certificate.expiresAt).getTime();
    if (Number.isNaN(expires)) {
      return { text: '—', color: 'grey' };
    }

    const days = Math.ceil((expires - Date.now()) / 86400000);
    if (days < 0) {
      return { text: 'Expired', color: 'error' };
    }
    if (days <= 14) {
      return { text: `${days}d left`, color: 'warning' };
    }
    return { text: `${days}d left`, color: 'success' };
  }

  @Watch('certificateId')
  public onCertificateIdChange(): void {
    this.certificate = null;
    this.error = null;
  }

  public onMouseEnter(): void {
    this.clearCloseTimer();
    if (this.menuOpen) {
      return;
    }
    this.clearOpenTimer();
    this.openTimer = setTimeout(() => {
      this.positionCard();
      this.menuOpen = true;
      this.loadCertificate();
    }, HOVER_OPEN_MS);
  }

  public onMouseLeave(): void {
    if (this.dialogOpen) {
      return;
    }
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.menuOpen = false;
    }, HOVER_CLOSE_MS);
  }

  public onCardEnter(): void {
    this.clearCloseTimer();
  }

  public beforeDestroy(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  public viewCertificate(): void {
    if (!this.certificate) {
      return;
    }
    this.dialogOpen = true;
    this.certificateDialog = 'view-certificate-dialog';
    this.certificateForDialog = this.certificate;
  }

  public showLogsDialog(): void {
    if (!this.certificate) {
      return;
    }
    this.dialogOpen = true;
    this.certificateLogsDialog = 'certificate-logs-dialog';
    this.certificateForLogsDialog = this.certificate;
  }

  public deleteCertificate(): void {
    if (!this.certificate) {
      return;
    }
    this.dialogOpen = true;
    this.deleteCertificateDialog = 'delete-certificate-dialog';
    this.certificateForDeleteDialog = this.certificate;
  }

  public async renewCertificate(): Promise<void> {
    if (!this.certificate) {
      return;
    }

    this.renewing = true;
    try {
      await this.certificateRepository.renewCertificate(this.certificate.id);
      this.$successDialog.show('Certificate renewal started!');
      certCache.delete(this.certificate.id);
      this.certificate = null;
      await this.loadCertificate(true);
    } catch (err) {
      const anyErr = err as any;
      this.$errorDialog.show(
        anyErr?.response?.data?.message
          || anyErr?.response?.data?.error
          || anyErr.message,
      );
    } finally {
      this.renewing = false;
    }
  }

  public async recreate(): Promise<void> {
    if (!this.certificate) {
      return;
    }
    await this.$router.push({
      name: 'certificate_create',
      query: { id: String(this.certificate.id) },
    });
  }

  public async newCertificate(): Promise<void> {
    await this.$router.push({ name: 'certificate_create' });
  }

  public onDialogClose(kind: 'certificate' | 'delete' | 'logs'): void {
    if (kind === 'certificate') {
      this.certificateDialog = '';
      this.certificateForDialog = false;
    } else if (kind === 'delete') {
      this.deleteCertificateDialog = '';
      this.certificateForDeleteDialog = false;
    } else {
      this.certificateLogsDialog = '';
      this.certificateForLogsDialog = false;
    }

    this.dialogOpen = Boolean(
      this.certificateDialog || this.deleteCertificateDialog || this.certificateLogsDialog,
    );
  }

  public onCertificateDeleted(): void {
    if (this.certificateId) {
      certCache.delete(this.certificateId);
    }
    this.certificate = null;
    this.menuOpen = false;
    this.dialogOpen = false;
    this.deleteCertificateDialog = '';
    this.certificateForDeleteDialog = false;
    this.$emit('deleted', this.websiteId);
  }

  private positionCard(): void {
    const el = this.$refs.anchor as HTMLElement | undefined;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 16);
    const maxHeight = Math.round(window.innerHeight * 0.6);
    const left = Math.min(
      Math.max(8, Math.round(rect.left)),
      Math.max(8, window.innerWidth - width - 8),
    );

    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const preferAbove = spaceBelow < 280 && spaceAbove > spaceBelow;

    if (preferAbove) {
      this.cardStyle = {
        top: 'auto',
        bottom: `${Math.round(window.innerHeight - rect.top + 6)}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.min(maxHeight, Math.max(160, spaceAbove))}px`,
      };
      return;
    }

    this.cardStyle = {
      top: `${Math.round(rect.bottom + 6)}px`,
      bottom: 'auto',
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${Math.min(maxHeight, Math.max(160, spaceBelow))}px`,
    };
  }

  private clearOpenTimer(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private async loadCertificate(force: boolean = false): Promise<void> {
    if (!this.hasCertificate) {
      this.certificate = null;
      this.loading = false;
      this.error = null;
      this.$nextTick(() => {
        if (this.menuOpen) {
          this.positionCard();
        }
      });
      return;
    }

    const id = this.certificateId as number;
    if (!force && this.certificate && this.certificate.id === id) {
      this.$nextTick(() => {
        if (this.menuOpen) {
          this.positionCard();
        }
      });
      return;
    }

    if (!force && certCache.has(id)) {
      this.certificate = this.withWebsiteId(certCache.get(id) as CertificateInterface);
      this.error = null;
      this.$nextTick(() => {
        if (this.menuOpen) {
          this.positionCard();
        }
      });
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const cert = await this.certificateRepository.getCertificateById(id);
      const normalized = this.withWebsiteId(cert);
      certCache.set(id, normalized);
      this.certificate = normalized;
    } catch (err) {
      const anyErr = err as any;
      this.certificate = null;
      this.error = anyErr?.response?.data?.message
        || anyErr?.response?.data?.error
        || anyErr.message
        || 'Could not load certificate.';
    } finally {
      this.loading = false;
      this.$nextTick(() => {
        if (this.menuOpen) {
          this.positionCard();
        }
      });
    }
  }

  private withWebsiteId(cert: CertificateInterface): CertificateInterface {
    if (cert.websiteId) {
      return cert;
    }
    return {
      ...cert,
      websiteId: this.websiteId,
    };
  }
}
